import dayjs from '@helpers/date';

/** One row of a weekly breakdown, as getSignupAttribution returns it. */
export interface SignupBreakdown { key: string; jobSeekers: number; employers: number }

export interface SignupWeek {
  /** The IST Monday the week starts on, yyyy-MM-dd. */
  weekStart: string;
  jobSeekers: number;
  employers: number;
  channels: SignupBreakdown[];
  utmSources?: SignupBreakdown[];
  utmMediums?: SignupBreakdown[];
  landingPages: SignupBreakdown[];
  methods: SignupBreakdown[];
}

export type Audience = 'jobSeekers' | 'employers' | 'both';
export type Dimension = 'channels' | 'landingPages' | 'methods';

/** A row of a weeks-across table: one value per week, oldest first, and their sum. */
export interface WeeklyRow { key: string; values: number[]; total: number; rollup?: boolean; summary?: boolean }

export const OTHER = 'Other';

/** Who a count is about. Both is a plain sum: a person is one or the other, never both. */
export const countFor = (r: { jobSeekers?: number | null; employers?: number | null }, audience: Audience): number => {
  const js = r.jobSeekers ?? 0;
  const em = r.employers ?? 0;
  return audience === 'jobSeekers' ? js : audience === 'employers' ? em : js + em;
};

/**
 * The column label for a week. The backend sends the current, unfinished week last, so it is
 * marked rather than read as a slow week next to six complete ones.
 */
export const weekLabel = (weekStart: string, isCurrent: boolean) => ({
  label: dayjs(weekStart).format('D MMM'),
  note: isCurrent ? 'so far' : undefined,
});

/**
 * Rows for one dimension: a key per row, a value per week, sorted by total.
 *
 * Keys that are zero for the chosen audience across every week are left out, so switching to
 * Employers does not leave a column of job seeker channels reading 0. Past `limit`, the tail is
 * summed into one "Other" row, which also absorbs a key that is literally called Other: two rows
 * with the same name would read as a bug.
 */
export const weeklyRows = (
  weeks: SignupWeek[],
  dimension: Dimension,
  audience: Audience,
  limit = Infinity,
): WeeklyRow[] => {
  const byKey = new Map<string, number[]>();
  weeks.forEach((w, i) => {
    (w[dimension] ?? []).forEach((b) => {
      const key = b.key || 'Unknown';
      if (!byKey.has(key)) byKey.set(key, new Array(weeks.length).fill(0));
      byKey.get(key)![i] += countFor(b, audience);
    });
  });

  const rows: WeeklyRow[] = [...byKey.entries()]
    .map(([key, values]) => ({ key, values, total: values.reduce((a, b) => a + b, 0) }))
    .filter((r) => r.total > 0)
    .sort((a, b) => b.total - a.total || a.key.localeCompare(b.key));

  if (rows.length <= limit) return rows;

  const named = rows.filter((r) => r.key !== OTHER);
  const top = named.slice(0, limit);
  const rest = [...named.slice(limit), ...rows.filter((r) => r.key === OTHER)];
  if (!rest.length) return top;
  const values = weeks.map((_, i) => rest.reduce((s, r) => s + r.values[i], 0));
  return [...top, { key: OTHER, values, total: values.reduce((a, b) => a + b, 0), rollup: true }];
};

/**
 * The Total row. Read from each week's own count, not summed from the rows: a sign-up with no
 * channel recorded is still a sign-up, and a rolled-up table must not quietly lose it.
 */
export const totalRow = (weeks: SignupWeek[], audience: Audience): WeeklyRow => {
  const values = weeks.map((w) => countFor(w, audience));
  return { key: 'Total', values, total: values.reduce((a, b) => a + b, 0), summary: true };
};
